import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, vModelSelect as _vModelSelect, vModelText as _vModelText } from "vue"

const _hoisted_1 = { class: "mt-6 bg-white border border-gray-200 rounded-xl shadow-sm" }
const _hoisted_2 = {
  key: 0,
  class: "flex justify-center py-10"
}
const _hoisted_3 = {
  key: 1,
  class: "p-5 space-y-4"
}
const _hoisted_4 = {
  key: 0,
  class: "text-xs text-gray-400 italic"
}
const _hoisted_5 = { class: "flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-t-lg" }
const _hoisted_6 = { class: "text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 shrink-0" }
const _hoisted_7 = { class: "text-sm font-medium text-gray-800 flex-1 min-w-0 truncate" }
const _hoisted_8 = { class: "text-xs text-gray-500 shrink-0" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = ["onClick"]
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 px-4 py-3" }
const _hoisted_13 = ["onUpdate:modelValue", "value"]
const _hoisted_14 = { class: "truncate" }
const _hoisted_15 = {
  key: 0,
  class: "text-xs text-gray-400 shrink-0"
}
const _hoisted_16 = {
  key: 1,
  class: "text-xs text-amber-600 shrink-0"
}
const _hoisted_17 = {
  key: 0,
  class: "px-4 pb-3 text-xs text-amber-600"
}
const _hoisted_18 = { class: "flex flex-col sm:flex-row gap-2" }
const _hoisted_19 = ["value"]
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "flex justify-end" }
const _hoisted_22 = ["disabled"]

import { ref, computed, onMounted } from 'vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

type Kind = 'direction' | 'ambassade' | 'user'
interface Candidate { id: number; nom: string }
interface DocType {
  id: number
  nom: string
  is_active: boolean
  direction: { nom: string } | null
  ambassade: { nom: string } | null
  scope: string
}
interface ApiException { kind: Kind; id: number; nom: string; document_type_ids: number[] }
interface Row { key: string; kind: Kind; id: number; nom: string; typeIds: number[] }

const MAX_CANDIDATES = 50


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentTypeAccessExceptionsPanel',
  setup(__props) {

const KIND_LABEL: Record<Kind, string> = { direction: 'Direction', ambassade: 'Ambassade', user: 'Utilisateur' }
const toast = useToast()
const loading = ref(true)
const saving = ref(false)
const rows = ref<Row[]>([])
const types = ref<DocType[]>([])
const directions = ref<Candidate[]>([])
const ambassades = ref<Candidate[]>([])
const users = ref<Candidate[]>([])

const newKind = ref<Kind>('direction')
const newTarget = ref<number | ''>('')
const userQuery = ref('')

const candidates = computed<Candidate[]>(() => {
  const source = { direction: directions.value, ambassade: ambassades.value, user: users.value }[newKind.value]
  const taken = new Set(rows.value.filter(r => r.kind === newKind.value).map(r => r.id))
  let list = source.filter(c => !taken.has(c.id))
  if (newKind.value === 'user') {
    const q = userQuery.value.trim().toLowerCase()
    if (q) list = list.filter(c => c.nom.toLowerCase().includes(q))
    list = list.slice(0, MAX_CANDIDATES)
  }
  return list
})

const toRows = (list: ApiException[]): Row[] =>
  list.map(e => ({ key: `${e.kind}${e.id}`, kind: e.kind, id: e.id, nom: e.nom, typeIds: [...e.document_type_ids] }))

const setAll = (row: Row, on: boolean) => {
  row.typeIds = on ? types.value.map(t => t.id) : []
}

const addRow = () => {
  if (!newTarget.value) return
  const source = { direction: directions.value, ambassade: ambassades.value, user: users.value }[newKind.value]
  const target = source.find(c => c.id === newTarget.value)
  if (!target) return
  // Liste blanche : on part de « tout coché » pour ne rien masquer par accident ;
  // l'admin décoche ce qu'il veut retirer.
  rows.value.push({
    key: `${newKind.value}${target.id}`,
    kind: newKind.value,
    id: target.id,
    nom: target.nom,
    typeIds: types.value.filter(t => t.is_active).map(t => t.id),
  })
  newTarget.value = ''
  userQuery.value = ''
}

const save = async () => {
  saving.value = true
  try {
    const payload = await apiFetch<{ exceptions: ApiException[] }>('/api/admin/document-types/access-exceptions/', {
      method: 'PUT',
      body: JSON.stringify({
        exceptions: rows.value.map(r => ({ kind: r.kind, id: r.id, document_type_ids: r.typeIds })),
      }),
    })
    rows.value = toRows(payload.exceptions)
    toast.success('Exceptions enregistrées', "Elles s'appliquent dès la prochaine rédaction")
  } catch (e: any) {
    toast.error('Erreur', e.message || "Impossible d'enregistrer les exceptions")
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    const [payload, docTypes, dirs, amb, usrs] = await Promise.all([
      apiFetch<{ exceptions: ApiException[] }>('/api/admin/document-types/access-exceptions/'),
      apiFetch<any[]>('/api/admin/document-types/'),
      apiFetch<Candidate[]>('/api/admin/directions/'),
      apiFetch<Candidate[]>('/api/admin/ambassades/'),
      apiFetch<any[]>('/api/admin/users/'),
    ])
    types.value = docTypes.map(t => ({
      id: t.id,
      nom: t.nom,
      is_active: t.is_active,
      direction: t.direction,
      ambassade: t.ambassade,
      scope: t.direction?.nom || t.ambassade?.nom || '',
    }))
    directions.value = dirs
    ambassades.value = amb
    users.value = usrs
      .filter(u => u.is_active)
      .map(u => ({ id: u.id, nom: u.full_name || u.username }))
      .sort((a, b) => a.nom.localeCompare(b.nom))
    rows.value = toRows(payload.exceptions)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger les exceptions de visibilité')
  } finally {
    loading.value = false
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "px-5 py-4 border-b border-gray-100" }, [
      _createElementVNode("h3", { class: "font-semibold text-[#22282F]" }, "Exceptions fines par direction, ambassade ou utilisateur"),
      _createElementVNode("p", { class: "text-sm text-gray-500 mt-1" }, [
        _createTextVNode(" Pour une direction, une ambassade ou une personne, choisissez "),
        _createElementVNode("strong", null, "exactement"),
        _createTextVNode(" les types de document qu'elle voit en rédaction. Cette liste remplace la règle générale ci-dessus. En cas de conflit : "),
        _createElementVNode("strong", null, "utilisateur > ambassade ou direction > règle générale"),
        _createTextVNode(". ")
      ])
    ], -1)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[4] || (_cache[4] = [
          _createElementVNode("div", { class: "w-6 h-6 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" }, null, -1)
        ])))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (!rows.value.length)
            ? (_openBlock(), _createElementBlock("p", _hoisted_4, "Aucune exception : la règle générale s'applique à tous."))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row, i) => {
            return (_openBlock(), _createElementBlock("div", {
              key: row.key,
              class: "border border-gray-200 rounded-lg"
            }, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("span", _hoisted_6, _toDisplayString(KIND_LABEL[row.kind]), 1),
                _createElementVNode("span", _hoisted_7, _toDisplayString(row.nom), 1),
                _createElementVNode("span", _hoisted_8, _toDisplayString(row.typeIds.length) + " / " + _toDisplayString(types.value.length) + " types", 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "text-xs text-gray-500 hover:text-gray-800 shrink-0",
                  onClick: ($event: any) => (setAll(row, true))
                }, "Tout", 8, _hoisted_9),
                _createElementVNode("button", {
                  type: "button",
                  class: "text-xs text-gray-500 hover:text-gray-800 shrink-0",
                  onClick: ($event: any) => (setAll(row, false))
                }, "Aucun", 8, _hoisted_10),
                _createElementVNode("button", {
                  type: "button",
                  class: "text-gray-400 hover:text-red-600 text-sm px-1",
                  title: "Retirer l'exception",
                  onClick: ($event: any) => (rows.value.splice(i, 1))
                }, "✕", 8, _hoisted_11)
              ]),
              _createElementVNode("div", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(types.value, (t) => {
                  return (_openBlock(), _createElementBlock("label", {
                    key: t.id,
                    class: "flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                  }, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": ($event: any) => ((row.typeIds) = $event),
                      type: "checkbox",
                      value: t.id,
                      class: "w-4 h-4 accent-[#22282F]"
                    }, null, 8, _hoisted_13), [
                      [_vModelCheckbox, row.typeIds]
                    ]),
                    _createElementVNode("span", _hoisted_14, _toDisplayString(t.nom), 1),
                    (t.scope)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_15, "(" + _toDisplayString(t.scope) + ")", 1))
                      : _createCommentVNode("", true),
                    (!t.is_active)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_16, "inactif"))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ]),
              (!row.typeIds.length)
                ? (_openBlock(), _createElementBlock("p", _hoisted_17, " Aucun type coché : cette cible ne verra aucun type de document en rédaction. "))
                : _createCommentVNode("", true)
            ]))
          }), 128)),
          _createElementVNode("div", _hoisted_18, [
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newKind).value = $event)),
              class: "px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#22282F]",
              onChange: _cache[1] || (_cache[1] = ($event: any) => {newTarget.value = ''; userQuery.value = ''})
            }, _cache[5] || (_cache[5] = [
              _createElementVNode("option", { value: "direction" }, "Une direction", -1),
              _createElementVNode("option", { value: "ambassade" }, "Une ambassade", -1),
              _createElementVNode("option", { value: "user" }, "Un utilisateur", -1)
            ]), 544), [
              [_vModelSelect, newKind.value]
            ]),
            (newKind.value === 'user')
              ? _withDirectives((_openBlock(), _createElementBlock("input", {
                  key: 0,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((userQuery).value = $event)),
                  type: "text",
                  placeholder: "Rechercher un nom…",
                  class: "px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F]"
                }, null, 512)), [
                  [_vModelText, userQuery.value]
                ])
              : _createCommentVNode("", true),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newTarget).value = $event)),
              class: "flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#22282F]"
            }, [
              _cache[6] || (_cache[6] = _createElementVNode("option", { value: "" }, "Choisir…", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(candidates.value, (c) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: c.id,
                  value: c.id
                }, _toDisplayString(c.nom), 9, _hoisted_19))
              }), 128))
            ], 512), [
              [_vModelSelect, newTarget.value]
            ]),
            _createElementVNode("button", {
              type: "button",
              disabled: !newTarget.value,
              class: "px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40",
              onClick: addRow
            }, "Ajouter", 8, _hoisted_20)
          ]),
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("button", {
              type: "button",
              disabled: saving.value,
              class: "px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 disabled:opacity-50",
              onClick: save
            }, _toDisplayString(saving.value ? 'Enregistrement…' : 'Enregistrer les exceptions'), 9, _hoisted_22)
          ])
        ]))
  ]))
}
}

})